import { RouterModule } from "@angular/router";

import { Component, computed, effect, inject, OnInit, resource, signal } from "@angular/core";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { GridApi, GridState, RowClassRules, SideBarDef } from "ag-grid-enterprise";

import { Article } from "src/app/_models";

import { CdkMenuModule } from "@angular/cdk/menu";
import { OverlayModule } from "@angular/cdk/overlay";
import { HttpParams } from "@angular/common/http";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { AgGridAngular } from "ag-grid-angular";
import { firstValueFrom } from "rxjs";
import { dateFormatter } from "src/app/_helpers/aggrid-renderer";
import { AG_GRID_LOCALE_DE } from "src/app/_helpers/aggrid.locale.de";
import { ArticleService } from "src/app/_services";
import { AgGridThemeService } from "src/app/_services/ag-grid-theme.service";
import { AgGridDeliveryNotesLinkRenderer } from "../_shared/ag-grid-renderer/ag-grid-delivery-notes-link.component";
import { AgGridGenericLinkRenderer } from "../_shared/ag-grid-renderer/ag-grid-generic-link.component";
import { ArticleSearchParams, ArtikelSearchParamsComponent } from "./artikel-search-params.component";
@Component({
  imports: [RouterModule, AgGridAngular, CdkMenuModule, OverlayModule, FontAwesomeModule, ArtikelSearchParamsComponent],
  selector: "hawk-artikel-search",
  templateUrl: "./artikel-search.component.html",
  styleUrls: ["./artikel-search.component.scss"],
  host: {
    class: "app-page",
  },
})
export class ArtikelSearchComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private articleService = inject(ArticleService);
  private queryParams = toSignal(this.route.queryParams);
  protected articleParams = computed((): ArticleSearchParams => {
    const query = this.queryParams();
    const keys: Array<keyof ArticleSearchParams> = ["status_id", "product_id", "rma", "serial_number"];
    const result: ArticleSearchParams = {};
    for (const key of keys) {
      if (key in query) {
        result[key] = query[key] as never;
      }
    }
    return result;
  });

  protected articlesResource = resource({
    request: this.articleParams,
    loader: ({ request }) => {
      if (!request || !Object.keys(request).length) {
        return Promise.resolve([]);
      }
      let searchParams = new HttpParams();
      if (request.status_id) {
        searchParams = searchParams.append("status_id", request.status_id);
      }
      if (request.product_id) {
        searchParams = searchParams.append("product_id", request.product_id);
      }
      if (request.rma) {
        searchParams = searchParams.append("rma", request.rma);
      }
      if (request.serial_number) {
        searchParams = searchParams.append("serial_number", request.serial_number);
      }
      if (request["ids"] as string[]) {
        for (const id of request["ids"]) {
          searchParams = searchParams.append(`ids[]`, id);
        }
      }
      return firstValueFrom(this.articleService.all(searchParams));
    },
  });

  public hawkTheme = inject(AgGridThemeService).getTheme();
  public get articles(): Article[] {
    return this.articlesResource.value();
  }

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ["columns", "filters"],
  };

  public locale = AG_GRID_LOCALE_DE;
  public storageKey = "deliveryNoteSearchStateSettings";

  public rowSelection: "single" | "multiple" = "multiple";
  public initialState?: GridState;

  public rowClassRules: RowClassRules = {
    // if tech on site, color row
    "tech-on-site": (params) => {
      return params.data.tech_on_site;
    },
  };

  public defaultColDef: ColDef = {
    filter: true,
    flex: 1,
  };

  // Column Definitions: Defines the columns to be displayed.
  public colDefs: ColDef[] = [
    {
      field: "rma",
      headerName: "RMA",
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: "rma",
        routerLink: ["/", "artikel", "@id"],
        icon: ["fa", "fa-cube"],
      },
    },
    {
      field: "template_description",
      headerName: "Bezeichnung",
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: "template_description",
        routerLink: ["/", "artikelvorlagen", "@template_id"],
        icon: ["fa", "fa-cubes"],
      },
    },
    {
      field: "bad_part",
      headerName: "Bad Part",
      cellRenderer: (params: any) => {
        if (params.data.bad_part) {
          return '<i class="fas fa-bug"></i>';
        }
        return "";
      },
    },
    {
      field: "product_category",
      headerName: "Produkt",
    },
    {
      field: "vendor",
      headerName: "Hersteller",
      hide: true,
    },
    {
      field: "serial_number",
      headerName: "Seriennummer",
    },
    {
      field: "delivery_date",
      headerName: "Lieferung",
      valueFormatter: dateFormatter("dd.MM.yyyy"),
      hide: true,
    },
    {
      field: "ext_delivery_note_number",
      headerName: "Liefer-Nr",
    },
    {
      field: "warehouse_label",
      headerName: "Lager",
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: "warehouse_label",
        routerLink: ["/", "lager", "@warehouse_id"],
      },
    },
    {
      field: "bin_label",
      headerName: "Platz",
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: "bin_label",
        routerLink: ["/", "lager", "@warehouse_id", "lagerort", "@bin_id", "alle"],
      },
    },
    {
      field: "location",
      headerName: "",
      cellRenderer: (params: any) => {
        if (params.data.location === "SHIPPING") {
          return `<div class="text-muted text-left">
                  <i class="fa fa-truck" ></i>
                </div>`;
        }
        return "";
      },
    },
    {
      field: "location_translated",
      headerName: "Status",
    },
    {
      field: "deliver_note_numbers",
      headerName: "Lieferscheine",
      cellRenderer: AgGridDeliveryNotesLinkRenderer,
    },
  ];

  private grid = signal<GridApi>(null);

  public constructor() {
    effect(() => {
      const grid = this.grid();
      const loading = this.articlesResource.isLoading();
      if (grid) {
        grid.setGridOption("loading", loading);
      }
    });
  }

  protected onGridReady(params: GridReadyEvent) {
    this.grid.set(params.api);
  }

  public ngOnInit(): void {
    this.initialState = this.getState();
  }

  public quicksearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.grid().setGridOption("quickFilterText", newValue);
  }

  public saveState() {
    let state = this.grid().getState();
    delete state["filter"];
    delete state.pagination.page;
    const serializedState = JSON.stringify(state);
    localStorage.setItem(this.storageKey, serializedState);
  }

  public deleteState() {
    localStorage.removeItem(this.storageKey);
  }

  public getState(): GridState {
    return JSON.parse(localStorage.getItem(this.storageKey));
  }

  public printState() {
    const stateJson = this.getState();
    console.log(localStorage.getItem(this.storageKey), stateJson);
  }
}
